import React from 'react'

import { Input as BaseInput } from '@base-ui/react/input'
import { tv } from 'tailwind-variants'

import { FieldRoot } from '@/components/ui/field'

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg'
  error?: boolean
  className?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const inputVariants = tv({
  slots: {
    root: 'w-full',
    box: [
      'bg-surface-white-50 flex w-full items-center gap-2 rounded-[4px] border border-solid transition-colors',
      'hover:border-primary-100',
      'border-surface-300',
      'has-[:focus]:border-primary-300',
      'has-[input[data-focused]]:border-primary-300',
      'has-[input[data-filled][data-focused]]:border-primary-200',
      'has-[input[aria-invalid=true]]:border-[#9d1717]',
      'has-[input[data-invalid]]:border-[#9d1717]',
      'has-[input:disabled]:bg-surface-disabled-bg has-[input:disabled]:border-surface-300 has-[input:disabled]:cursor-not-allowed',
      'has-[input[data-disabled]]:bg-surface-disabled-bg has-[input[data-disabled]]:border-surface-300'
    ],
    input: [
      'min-w-0 flex-1 appearance-none border-none bg-transparent outline-none',
      'text-surface-body font-normal',
      'placeholder:text-surface-body placeholder:font-normal',
      '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
      'focus:text-surface-title-50',
      'data-[focused]:text-surface-title-50',
      'aria-invalid:text-[#9d1717]',
      'data-[invalid]:text-[#9d1717]',
      'disabled:text-surface-body disabled:cursor-not-allowed disabled:font-normal',
      'data-[disabled]:text-surface-body data-[disabled]:cursor-not-allowed data-[disabled]:font-normal'
    ],
    leftIconSlot: 'flex shrink-0 items-center',
    rightIconSlot: 'flex shrink-0 items-center'
  },
  variants: {
    size: {
      sm: {
        box: 'h-[31px] px-4',
        input: 'text-caption-lg py-2 font-bold placeholder:font-bold',
        leftIconSlot: 'size-[18px] text-[12px]',
        rightIconSlot: 'size-[18px] text-[12px]'
      },
      md: {
        box: 'h-[45px] px-4',
        input: 'text-body-md py-3',
        leftIconSlot: 'size-[21px] text-[14px]',
        rightIconSlot: 'size-[21px] text-[14px]'
      },
      lg: {
        box: 'p-4',
        input: 'text-body-lg',
        leftIconSlot: 'size-6 text-[16px]',
        rightIconSlot: 'size-6 text-[16px]'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

/**
 * @deprecated Use `Input` from `@alternative/acqua/molecules` instead. The acqua-ui V2 Input is being phased out in favor of the acqua design system.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { size = 'md', error, className, leftIcon, rightIcon, disabled, ...props },
    ref
  ) => {
    const styles = inputVariants({ size })
    return (
      <FieldRoot className={styles.root()} invalid={error} disabled={disabled}>
        <div className={styles.box({ className })}>
          {leftIcon && (
            <span className={styles.leftIconSlot()}>{leftIcon}</span>
          )}
          <BaseInput
            ref={ref}
            data-slot='input'
            aria-invalid={error || undefined}
            disabled={disabled}
            className={styles.input()}
            {...props}
          />
          {rightIcon && (
            <span className={styles.rightIconSlot()}>{rightIcon}</span>
          )}
        </div>
      </FieldRoot>
    )
  }
)

Input.displayName = 'V2Input'
